/**
 * Music Generation Service
 *
 * Handles async theme music generation via queue-callback pattern.
 * Returns jobId immediately - use useQueueJob hook to watch for completion.
 *
 * @module musicService
 * @version 1.0 - Issue #601 Theme Music Generation
 */
import { logger } from '@musedrops/shared';
import { supabase } from './supabase';

interface GenerateThemeMusicParams {
  showSlug: string;
}

interface GenerateMusicResponse {
  success: boolean;
  jobId?: number;
  error?: string;
}

/**
 * Start theme music generation job (async - returns jobId immediately).
 * Subscribe to job completion using useQueueJob hook.
 * When complete, job output contains { path: "shows/slug/theme.mp3" }
 */
export const generateShowThemeMusic = async (
  params: GenerateThemeMusicParams
): Promise<GenerateMusicResponse> => {
  try {
    const { data, error } = await supabase.functions.invoke(
      'gen-show-theme-music',
      {
        body: {
          showSlug: params.showSlug,
          trigger: 'manual', // UI-initiated jobs get priority
        },
      }
    );

    if (error) {
      logger.error('Theme music generation error', {
        error,
        showSlug: params.showSlug,
      });
      throw new Error(error.message || 'Failed to generate theme music');
    }

    // Check if the response is an error from the generation function
    if (data && typeof data === 'object' && data.success === false) {
      logger.error('Theme music generation failed', {
        error: data.error,
        showSlug: params.showSlug,
      });
      throw new Error(`Theme music generation failed: ${data.error}`);
    }

    // Extract jobId from queue-based response
    const jobId = data?.result?.jobId;

    if (!jobId || typeof jobId !== 'number') {
      logger.error('Unable to extract jobId from response', {
        data,
        showSlug: params.showSlug,
      });
      throw new Error('Invalid response from theme music generation function');
    }

    logger.info('Theme music generation job created', {
      jobId,
      showSlug: params.showSlug,
    });

    return {
      success: true,
      jobId,
    };
  } catch (error) {
    logger.error('Theme music generation service error', {
      error,
      showSlug: params.showSlug,
    });
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};
